import { Chip } from "@firecms/ui";
import { useCallback, useMemo, useState } from "react";

import { fetchLocations, type ModMedLocationView } from "@/services/modmed";

import { CopyMenu } from "../components/CopyMenu";
import { Panel, PanelToolbar } from "../components/Panel";
import { QueryRowsInput } from "../components/QueryRowsInput";
import { EmptyRow, IdBadge, TableContainer, TBody, TD, TH, THead, TR } from "../components/Table";
import { usePanelFetch } from "../hooks";
import type { QueryRow } from "../types";
import { rowsToParams } from "../utils";

const DISABLED_REASON = "Save the document before fetching ModMed resources.";

const DEFAULT_ROWS: QueryRow[] = [
  { key: "_count", value: "50" },
  { key: "status", value: "active" },
];

type Props = { customerId?: string; disabled: boolean };

export const LocationsPanel = ({ customerId, disabled }: Props) => {
  const [rows, setRows] = useState<QueryRow[]>(DEFAULT_ROWS);
  const [search, setSearch] = useState("");

  const fetcher = useCallback(
    () => fetchLocations(customerId!, rowsToParams(rows)),
    [customerId, rows],
  );
  const { state, run } = usePanelFetch<ModMedLocationView>(fetcher);

  const filtered = useMemo(() => {
    if (!state.results) return null;
    const q = search.trim().toLowerCase();
    if (!q) return state.results;
    return state.results.filter(
      (l) =>
        l.id.toLowerCase().includes(q) ||
        l.name.toLowerCase().includes(q) ||
        l.status.toLowerCase().includes(q),
    );
  }, [state.results, search]);

  return (
    <Panel
      title="Locations"
      count={state.results?.length}
      disabled={disabled}
      disabledReason={DISABLED_REASON}
      queryFilters={
        <QueryRowsInput rows={rows} onChange={setRows} suggestedKeys={["_count", "status"]} />
      }
      toolbar={
        <PanelToolbar
          search={search}
          onSearchChange={setSearch}
          fetchLabel="Fetch locations"
          onFetch={run}
          disabled={disabled}
          state={state}
        />
      }
    >
      {filtered !== null && (
        <TableContainer>
          <THead>
            <tr>
              <TH className="w-28">ID</TH>
              <TH>Name</TH>
              <TH className="w-24">Status</TH>
              <TH>Address</TH>
              <TH className="w-10" />
            </tr>
          </THead>
          <TBody>
            {filtered.length === 0 ? (
              <EmptyRow colSpan={5} label="locations" />
            ) : (
              filtered.map((loc) => {
                const cityLine = [loc.address.city, loc.address.state, loc.address.postalCode]
                  .filter(Boolean)
                  .join(", ");
                return (
                  <TR key={loc.id}>
                    <TD>
                      <IdBadge value={loc.id} />
                    </TD>
                    <TD className="font-medium">{loc.name}</TD>
                    <TD>
                      <Chip
                        size="small"
                        colorScheme={loc.status === "active" ? "greenLighter" : "grayLighter"}
                      >
                        {loc.status}
                      </Chip>
                    </TD>
                    <TD className="text-text-secondary dark:text-text-secondary-dark text-xs">
                      <div>{loc.address.line.join(", ")}</div>
                      <div>{cityLine}</div>
                    </TD>
                    <TD>
                      <CopyMenu
                        options={[
                          { label: "ID", value: loc.id },
                          { label: "Name", value: loc.name },
                          { label: "Status", value: loc.status },
                          {
                            label: "Address",
                            value: [...loc.address.line, cityLine].filter(Boolean).join(", "),
                          },
                        ]}
                      />
                    </TD>
                  </TR>
                );
              })
            )}
          </TBody>
        </TableContainer>
      )}
    </Panel>
  );
};
